<template>
  <div
    :id="'question-' + (index ? index : '')"
    class="part-editor mb-2"
  >
      <div class="workspace-block">
          <div class="d-flex align-start">
            <div class="flex-grow-1 min-width-0">
              <Editor
                :value="question.question"
                :placeholder="$t('article.placeholder.question')"
                :words-to-highlight="wordsToHighlight"
                :allow-formatting="false"
                should-listen-for-change
                is-heading
                :waiting-for-ai="Boolean(waitingForAi?.question)"
                :disabled="disabled"
                @input="updateQuestion('question', $event)"
              />
            </div>
            <v-expand-x-transition>
              <ArticlePartExtend
                v-if="!shareLocked && question.question && !question.answer"
                class="heading-extend-btn"
                part="question"
                :uid="question.uid"
                :loading.sync="loadingExtend"
                :words-generated-elsewhere="wordsGeneratedInA"
                :force-prompt="strippedQuestion"
                :type="partExtendFromQuestion"
                hide-extensions
                lang-path="question"
                @wordsGenerated="count => wordsGeneratedInQ = count"
                @generatingExtensionValueId="createWatcherForA"
                @append="appendToAnswer"
              />
            </v-expand-x-transition>
          </div>
          <div class="d-flex flex-wrap align-center text-caption-primary mx-1">
            <span>
              {{ $t('article.questions.question') + ' ' + (index + 1) }}
            </span>
            <VerticalDivider />
            <span>
              {{ $t('article.questions.title') }}
            </span>
          </div>
          <div class="mt-3">
            <Editor
              :value="question.answer"
              :placeholder="$t('article.placeholder.answer')"
              :words-to-highlight="wordsToHighlight"
              should-listen-for-change
              :waiting-for-ai="Boolean(waitingForAi?.answer)"
              :disabled="disabled"
              @input="updateQuestion('answer', $event)"
            />
            <v-expand-transition>
              <ArticlePartExtend
                v-if="!shareLocked && showExtendWithAi && !waitingForAi?.answer"
                part="question"
                :uid="question.uid"
                :loading.sync="loadingExtend"
                :words-generated-elsewhere="wordsGeneratedInQ"
                :watch-for-extension-value="watchForExtenstionValue"
                lang-path="answer"
                :disabled="disabled"
                @wordsGenerated="count => wordsGeneratedInA = count"
                @append="appendToAnswer"
              />
            </v-expand-transition>
            <div class="d-flex flex-wrap align-center text-caption-primary mx-1">
              <span>
                {{ $t('article.questions.answer') }}
              </span>
              <VerticalDivider />
              <ArticlePartExtendToggle
                v-if="!shareLocked"
                v-model="showExtendWithAi"
                lang-path="answer"
              />
              <VerticalDivider v-if="!shareLocked" />
              <v-chip
                v-if="!shareLocked"
                small
                outlined
                :disabled="$store.getters.articleAiLoading.loadingRatioQuestions > 0"
                class="chip-text-primary"
                @click="$emit('add-question')"
              >
                <v-icon class="mr-2">
                  playlist_add
                </v-icon>
                {{ $t('article.addQuestion') }}
              </v-chip>
            </div>
          </div>
        </div>
      <div v-if="!shareLocked" class="block-remove">
        <ArticlePartRemoveDialog
          part="question"
          :title="question.question"
          :content="question.answer"
          lang-path="article.removeQuestion"
          :disabled="disabled"
          @removePart="removeQuestion"
        />
      </div>
    </div>
</template>

<script lang="ts">
    import Vue, {PropType} from 'vue';
    import Editor from '@/components/Editor.vue';
    import { ArticleQuestion } from "@/model/Article";
    import VerticalDivider from "@/components/VerticalDivider.vue";
    import ArticlePartRemoveDialog from "@/components/article/ArticlePartRemoveDialog.vue";
    import ArticlePartExtendToggle from "@/components/article/ArticlePartExtendToggle.vue";
    import ArticlePartExtend from "@/components/article/ArticlePartExtend.vue";
    import { Store } from "@/plugins/Store";
    import { stripTagsAndEntities } from '@/utils/string';
    import { OpenAiExpandPartType } from "@/model/OpenAI";

    export default Vue.extend({
        name: 'ArticleQuestionsEditor',
        components: {ArticlePartExtend, ArticlePartExtendToggle, ArticlePartRemoveDialog, VerticalDivider, Editor},
        props: {
            index: {
                type: Number,
                default: 0
            },
            wordsToHighlight: {
                type: Object as PropType<{ [key: string]: string[] }>,
                default: () => ({})
            },
            disabled: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            showExtendWithAi: true,
            watchForExtenstionValue: '',
            wordsGeneratedInQ: 0,
            wordsGeneratedInA: 0,
            loadingExtend: false,
            partExtendFromQuestion: OpenAiExpandPartType.FROM_QUESTION
        }),
        computed: {
            question: function (): ArticleQuestion {
                return this.$store.getters.article.questions[this.index];
            },
            waitingForAi: function (): ArticleQuestion {
                return this.$store.getters.articleAiLoading.questions[this.index];
            },
            strippedQuestion: function (): string {
                return stripTagsAndEntities(this.question.question);
            },
            shareLocked(): boolean {
                return !!this.$store.getters.sharePreview;
            },
        },
        methods: {
            updateQuestion: function (key: 'question' | 'answer', value: string): void {
                this.$store.commit('setArticleQuestion', {
                    index: this.index,
                    questionKey: key,
                    value: value
                });
            },
            createWatcherForA: function (valueId: string) {
                this.watchForExtenstionValue = valueId;
            },
            removeQuestion: function (): void {
                this.$store.commit('removeArticleQuestion', this.index);
            },
            appendToAnswer: function (text: string): void {
                Store.commit('setArticleQuestion', {
                    index: this.index,
                    questionKey: 'answer',
                    value: this.question.answer + (this.question.answer ? '<br><br>' : '') + text
                });
            }
        }
    });
</script>

<style scoped lang="sass">
.part-editor
  position: relative
  width: 100%

.min-width-0
  min-width: 0

.heading-extend-btn
  flex: 0 0 auto
  margin-left: 12px
  padding-top: 2px
  max-width: 280px

.block-remove
  position: absolute
  top: 4px
  right: -40px
  z-index: 2

  ::v-deep .side-button
    margin-left: 0 !important
</style>
